"use client";
import { toast } from "sonner";
import { api } from "@/convex/_generated/api";
import { useMutation, useQuery } from "convex/react";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { useMemo, useState, useContext, useCallback, createContext, type ReactNode, useEffect } from "react";
import { produce } from "immer";
import { Plan } from "@/convex/schemas/planSchema";
import { useRouter } from "next/navigation";
import { getWorkflow, Workflow } from "../_config/workflows.config";

interface PlanContextType {
  /**
   * State and setters
   */
  editingPlan: Plan | null;
  updateEditingPlan: (updater: (draft: Plan) => void) => void;

  selectedPlanId: Id<"plans"> | null | undefined;
  setSelectedPlanId: (id: Id<"plans"> | null) => void;

  planToDelete: Id<"plans"> | null;
  setPlanToDelete: (id: Id<"plans"> | null) => void;

  isNewPlan: boolean;

  workflow: Workflow | null | undefined;

  /**
   * Actions
   */
  handleDeletePlan: (planId: Id<"plans">) => Promise<void>;
  handleTogglePlanActive: (planId: Id<"plans">) => Promise<void>;

  savePlan: () => Promise<void>;
  resetPlanForm: () => void;

  handleAssociateTemplate: (templateId: Id<"emailTemplates">, planId: Id<"plans"> | null) => Promise<void>;

  /**
   * DB objects
   */
  plans: Doc<"plans">[] | undefined | null;
  planTemplates: Doc<"emailTemplates">[] | undefined | null;
}

const PlanContext = createContext<PlanContextType | null>(null);

interface PlanProviderProps {
  children: ReactNode;
}

// Default plan values
const createDefaultPlan = (): Plan => ({
  name: "",
  active: false,
  cadence: "weekly",
  dayOfWeek: "monday",
  type: "automated",
});

export function PlanProvider({ children }: PlanProviderProps) {
  const plans = useQuery(api.plans.queries.getPlans, {});

  const [selectedPlanId, setSelectedPlanId] = useState<Id<"plans"> | null | undefined>(null);

  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<Id<"plans"> | null>(null);

  // Query for templates associated with the selected plan
  const planTemplates = useQuery(
    api.emailTemplates.queries.getTemplatesByPlan,
    selectedPlanId ? { planId: selectedPlanId } : "skip",
  );

  const selectedPlan = useQuery(api.plans.queries.getPlan, {
    planId: selectedPlanId ?? undefined,
  });

  const createPlan = useMutation(api.plans.mutations.createPlan);
  const updatePlan = useMutation(api.plans.mutations.updatePlan);
  const deletePlan = useMutation(api.plans.mutations.deletePlan);
  const togglePlanActive = useMutation(api.plans.mutations.togglePlanActive);
  const associateTemplateWithPlan = useMutation(api.plans.mutations.associateTemplateWithPlan);

  // Function to update editing plan using immer
  const updateEditingPlan = useCallback((updater: (draft: Plan) => void) => {
    setEditingPlan((current) => {
      if (!current) return null;
      return produce(current, updater);
    });
  }, []);

  const resetPlanForm = () => {
    setSelectedPlanId(undefined);
  };

  const router = useRouter();

  const savePlan = async () => {
    if (!editingPlan) {
      toast.error("No plan data to save");
      return;
    }

    try {
      if (selectedPlanId) {
        await updatePlan({
          planId: selectedPlanId,
          name: editingPlan.name,
          cadence: editingPlan.cadence,
          dayOfWeek: editingPlan.dayOfWeek,
          active: editingPlan.active,
        });
        toast.success("Plan updated!");
      } else {
        const planId = await createPlan({
          name: editingPlan.name,
          cadence: editingPlan.cadence,
          dayOfWeek: editingPlan.dayOfWeek,
          active: editingPlan.active,
          type: editingPlan.type,
        });
        toast.success("Plan created!");
        router.push(`/admin/plans/${planId}`);
      }
    } catch (error: any) {
      toast.error("Failed to save plan: " + error.message);
    }
  };

  const handleDeletePlan = async (planId: Id<"plans">) => {
    try {
      await deletePlan({ planId: planId });
      if (selectedPlanId === planId) {
        resetPlanForm();
        setSelectedPlanId(null);
      }
      toast.success("Plan deleted successfully! Associated templates have been orphaned.");
      setPlanToDelete(null);
    } catch (error: any) {
      toast.error("Failed to delete plan: " + error.message);
    }
  };

  const handleTogglePlanActive = async (planId: Id<"plans">) => {
    try {
      await togglePlanActive({ planId: planId });
      toast.success("Plan status updated!");
    } catch (error: any) {
      toast.error("Failed to update plan status: " + error.message);
    }
  };

  const handleAssociateTemplate = async (templateId: Id<"emailTemplates">, planId: Id<"plans"> | null) => {
    if (!planId) {
      toast.error("No plan selected");
      return;
    }
    try {
      await associateTemplateWithPlan({
        templateId: templateId,
        planId: planId,
      });
      toast.success(planId ? "Template associated with plan!" : "Template removed from plan!");
    } catch (error: any) {
      toast.error("Failed to associate template: " + error.message);
    }
  };

  const isNewPlan = useMemo(() => selectedPlanId === null, [selectedPlanId]);

  useEffect(() => {
    if (selectedPlan) {
      setEditingPlan({
        name: selectedPlan.name,
        active: selectedPlan.active,
        cadence: selectedPlan.cadence,
        dayOfWeek: selectedPlan.dayOfWeek,
        type: selectedPlan.type,
      });
    } else {
      setEditingPlan(createDefaultPlan());
    }
  }, [selectedPlan]);

  return (
    <PlanContext.Provider
      value={{
        /**
         * State and setters
         */
        editingPlan,
        updateEditingPlan,

        selectedPlanId,
        setSelectedPlanId,

        planToDelete,
        setPlanToDelete,

        isNewPlan,

        workflow: getWorkflow(editingPlan?.name),

        /**
         * Actions
         */
        savePlan,
        resetPlanForm,

        handleDeletePlan,
        handleTogglePlanActive,
        handleAssociateTemplate,

        /**
         * DB objects
         */
        plans,
        planTemplates,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}

export function withPlan(): PlanContextType {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error("withPlan must be used within a PlanProvider");
  }
  return context;
}
